import { BadgeElement, BadgeElementType } from "sharedTypes/badge"

/**
 * Hit-testing for shape elements (rectangles and ovals)
 * Determines if a click is on the visual part of the shape (stroke or fill)
 */
export type HitPoint = { clientX: number; clientY: number; currentTarget: Element }

export const isClickOnShapeVisualPart = (
  e: HitPoint,
  element: BadgeElement,
): boolean => {
  if (element.type !== BadgeElementType.RECTANGLE && element.type !== BadgeElementType.OVAL) {
    return true // Non-shape elements use full bounding box
  }

  // Get click position relative to the element
  const rect = e.currentTarget.getBoundingClientRect()
  const clickX = e.clientX - rect.left
  const clickY = e.clientY - rect.top

  // Convert to element coordinate space
  const elementClickX = (clickX / rect.width) * element.width
  const elementClickY = (clickY / rect.height) * element.height

  const shapeData = element.data // TypeScript narrows this automatically
  const strokeThickness = shapeData.thickness || 1
  const hasFill = shapeData.fillColor && shapeData.fillColor !== "transparent"

  // Expand hit area for easier selection (5mm tolerance)
  const hitAreaTolerance = 5

  if (element.type === BadgeElementType.RECTANGLE) {
    // For rectangle: check if click is on stroke or fill
    const isInsideElement = elementClickX >= 0
      && elementClickX <= element.width
      && elementClickY >= 0
      && elementClickY <= element.height

    if (!isInsideElement) return false

    // If has fill, entire area is clickable
    if (hasFill) return true

    // If no fill, expand the stroke hit area for easier selection
    const expandedStrokeWidth = strokeThickness + hitAreaTolerance
    const isOnStroke = elementClickX <= expandedStrokeWidth
      || elementClickX >= element.width - expandedStrokeWidth
      || elementClickY <= expandedStrokeWidth
      || elementClickY >= element.height - expandedStrokeWidth

    return isOnStroke
  }

  if (element.type === BadgeElementType.OVAL) {
    // For oval: check if click is within ellipse
    const centerX = element.width / 2
    const centerY = element.height / 2
    const radiusX = element.width / 2
    const radiusY = element.height / 2

    // Distance from center (normalized by radii)
    const normalizedDistance = ((elementClickX - centerX) / radiusX) ** 2
      + ((elementClickY - centerY) / radiusY) ** 2

    // If has fill, entire ellipse area is clickable
    if (hasFill && normalizedDistance <= 1) return true

    // If no fill, expand the stroke hit area for easier selection
    const outerRadius = 1 + (hitAreaTolerance / Math.min(radiusX, radiusY))
    const innerRadius = Math.max(
      0,
      1 - ((strokeThickness + hitAreaTolerance) / Math.min(radiusX, radiusY)),
    )

    return normalizedDistance <= outerRadius && normalizedDistance >= innerRadius
  }

  return true
}

/**
 * Generate CSS transform string for element transforms
 */
export const getElementTransformStyle = (
  element: BadgeElement,
  preview: { rotation: number },
  isDragging: boolean,
  dragType: "move" | "resize" | "rotate" | null,
): string | undefined => {
  const transforms: string[] = []

  // Use preview rotation during drag, otherwise use element rotation
  const rotation = isDragging && dragType === "rotate"
    ? preview.rotation
    : element.transforms?.rotate || 0
  if (rotation !== 0) {
    transforms.push(`rotate(${rotation}deg)`)
  }

  if (element.transforms?.flipHorizontal) {
    transforms.push("scaleX(-1)")
  }
  if (element.transforms?.flipVertical) {
    transforms.push("scaleY(-1)")
  }

  return transforms.length > 0 ? transforms.join(" ") : undefined
}

/**
 * Generate border style for element
 */
export const getBorderStyle = (element: BadgeElement, scaleFactor: number) => {
  if (!element.border || element.border.width === 0) return {}

  const borderWidthPx = element.border.width * scaleFactor

  return {
    borderWidth: `${borderWidthPx}px`,
    borderStyle: element.border.style,
    borderColor: element.border.color,
  }
}

/**
 * Normalize rotation to -360 to 360 range
 */
export const normalizeRotation = (angle: number): number => {
  if (Math.abs(angle) > 360) {
    const sign = angle >= 0 ? 1 : -1

    return sign * (Math.abs(angle) % 360)
  }

  return angle
}
